"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.workingDaySchema = exports.DAY_VERDICTS = void 0;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The working day — C44, the kernel the rest of Phase 3 reads.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One question: **what was expected of this person on this date, and how does what they logged
 * compare?** The Logolás dashboard draws a grid of these, the home screen sums them, a leave
 * request costs the working days in its range, and every one of those is wrong if the answer is
 * computed twice.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * ⚠️⚠️ **SEVEN VERDICTS, AND THE MOCKUP'S OWN NAMES.** `DAY_KIND` in the prototype: `under`, `ok`,
 * `over`, `off`, `future`, `free`, `leave` — *kevés, terv szerint, terv felett, pihenőnap, még
 * hátra van, nincs napi terv, szabadság*. They are not severities and they do not order: `off` is
 * a Sunday, `free` is *"this person has no plan we can compare against"*, and telling them apart is
 * the difference between a quiet grid and one that flags every weekend.
 */
/**
 * ⚠️⚠️ **`holiday` IS THE EIGHTH, ADDED 15 Sep, AND IT WAS `off` BEFORE.** A public holiday and a
 * company day off both zero the day, so the kernel called them *pihenőnap* — the same word as a
 * Sunday. Reported as *"if a day is a holiday, the day still shows up as a normal day on logging"*:
 * a workspace closed on 20 August looked exactly like the weekend either side of it, and nothing on
 * the screen said why nobody was expected to log.
 *
 * ⚠️ **NOT the third kind.** An *áthelyezett munkanap* ADDS a working day — it is an ordinary
 * working Saturday and takes an ordinary verdict.
 */
exports.DAY_VERDICTS = [
    'under',
    'ok',
    'over',
    'off',
    'future',
    'free',
    'leave',
    'holiday',
];
exports.workingDaySchema = zod_1.z.object({
    date: wireDateSchema,
    /**
     * What was expected, in hours. `null` means there is no plan to compare against — nobody has been
     * given a schedule that covers this date — which is `free`, and is NOT the same as `0`.
     *
     * ⚠️⚠️ **ALSO `null` FOR EVERY DAY OF A `flex` SCHEDULE — P3-7.** A weekly frame says what a week
     * owes and nothing whatever about a Tuesday, and drawing the frame divided by five was the
     * product asserting a working pattern the schema never held. This is the field a screen renders.
     *
     * ⚠️ A string, like every other `numeric` on the wire.
     */
    planned: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **AN AGGREGATION TERM, AND NOTHING MAY DRAW IT AS A DAILY PLAN.** It equals `planned` for
     * a `fix` schedule, and for a `flex` one it carries the frame's share of a weekday so that a
     * window's totals add back up to the frame. A screen rendering this instead of `planned` puts
     * P3-6 straight back, one cell at a time.
     */
    expected: zod_1.z.string().nullable(),
    /**
     * Whether the person works this day at all — the schedule and the calendar together.
     *
     * ⚠️⚠️ **EVERY *how many days does this cost* IN THE PHASE COUNTS THIS**, never `planned > 0`:
     * that test reads false for every flex working day now, so a fortnight off would cost nothing.
     */
    workingDay: zod_1.z.boolean(),
    /** What was logged. `'0.00'` when nothing was. */
    logged: zod_1.z.string(),
    /**
     * ⚠️⚠️ **WHETHER THEY WERE ON APPROVED LEAVE — 28 Oct 2026.** The verdict alone could not say: a
     * leave day somebody logged on anyway reads `over`, not `leave`. And leave is NEVER a shortfall
     * (the product owner: *"we never treat a leave day as a short day"*), so every sum of what a span
     * expected leaves these days out, and every sum of leave counts them, whatever was logged.
     */
    onLeave: zod_1.z.boolean(),
    verdict: zod_1.z.enum(exports.DAY_VERDICTS),
    /** The calendar entry that shaped the day, if one did. Its name is what a tooltip shows. */
    holidayName: zod_1.z.string().nullable(),
});
//# sourceMappingURL=working-day.js.map